<template>
  <div class="stock-transactions-page">
    <div class="d-flex align-center justify-end mb-4">
      <ViewToggle v-model="activeView" :options="viewOptions" />
    </div>
    <v-alert v-if="!arenaId" type="info" dense class="mb-4">
      {{ $t('entities.stockTransactions.selectArenaReminder') }}
    </v-alert>
    <StockTransactionUpsertDialog
      :arena-variants="arenaVariants"
      :is-loading="isSubmittingTransaction"
      :is-opened="isUpsertDialogOpen"
      :arena-id="arenaId"
      :suppliers="suppliers"
      :is-editing="isTransactionBeingEdited"
      :transaction="stockTransactionToUpsert"
      :transaction-type="selectedTransactionType"
      :users="userLabels"
      :server-error="upsertError"
      :initial-variant-id="initialVariantId"
      @dialog:close="closeUpsertDialog"
      @stock-transaction:upsert="upsertStockTransaction" />
    <template v-if="activeView === 'transactions'">
      <StockTransactionsTableList
        :arena-variants="arenaVariants"
        :suppliers="suppliers"
        :transactions="stockTransactions"
        :is-loading="areStockTransactionsLoading"
        :items-per-page="pagination.per_page"
        :current-page="pagination.current_page"
        :total-transactions="pagination.total"
        :transaction-types="transactionTypes"
        :users="userLabels"
        :initial-variant-id="transactionFilters.variantId"
        :initial-created-from="transactionFilters.createdFrom"
        :initial-created-until="transactionFilters.createdUntil"
        class="mt-6"
        @pagination:change="handleStockTransactionsPaginationChange"
        @transaction:open="openToolbarTransaction"
        @transaction:edit="openEditTransaction"
        @transaction:view="openTransactionDetails" />
    </template>
    <template v-else>
      <StockOverviewTable
        :items="stockOverviewItems"
        :is-loading="isStockOverviewLoading"
        :initial-start-date="overviewDateFilters.startDate"
        :initial-end-date="overviewDateFilters.endDate"
        :initial-day-type="overviewDayType"
        class="mt-6"
        @filter:date-change="handleOverviewDateFilterChange"
        @filter:day-type-change="handleOverviewDayTypeChange"
        @transactions:view="handleViewTransactions"
        @transaction:open="handleOverviewTransactionOpen" />
    </template>
    <StockTransactionDetailsDialog
      :arena-variants="arenaVariants"
      :suppliers="suppliers"
      :users="userLabels"
      :invoices="invoices"
      :arena-id="arenaId"
      :transaction="transactionToView"
      :is-opened="isDetailsDialogOpen"
      @dialog:close="closeDetailsDialog" />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ArenaProduct from '@/models/shop/ArenaProduct'
import ArenasApi from '@/services/setup/ArenasApi'
import Supplier from '@/models/shop/Supplier'
import Invoice from '@/models/shop/InvoiceV2'
import StockTransaction from '@/models/shop/StockTransaction'
import StockTransactionsTableList from './TableList.vue'
import StockTransactionUpsertDialog from './upsert-dialog/index.vue'
import StockTransactionDetailsDialog from './DetailsDialog.vue'
import StockOverviewTable from './OverviewTable.vue'
import ViewToggle from './ViewToggle.vue'
import UsersApi from '@/services/users/UsersApi'

const resetPagination = () => ({
  current_page: 1,
  per_page: 10,
  total: 0,
  last_page: 1,
  from: 0,
  to: 0,
})

const defaultTransactionFilters = () => ({
  transactionType: null,
  variantId: null,
  userId: null,
  createdFrom: null,
  createdUntil: null,
})

const systemManagedTransactionTypes = [
  {
    name: 'buy',
    l10n_name_key: 'shop.BUY.trans_name',
    l10n_description_key: 'shop.BUY.trans_desc',
    user_managed: false,
    code: 'BUY',
  },
  {
    name: 'restore',
    l10n_name_key: 'shop.RESTORE.trans_name',
    l10n_description_key: 'shop.RESTORE.trans_desc',
    user_managed: false,
    code: 'RESTORE',
  },
]

export default {
  name: 'StockTransactions',
  components: {
    StockTransactionsTableList,
    StockTransactionUpsertDialog,
    StockTransactionDetailsDialog,
    StockOverviewTable,
    ViewToggle,
  },
  data() {
    return {
      areArenaVariantsLoading: false,
      areInvoicesLoading: false,
      areStockTransactionsLoading: false,
      areSuppliersLoading: false,
      areTransactionTypesLoading: false,
      arenaVariants: [],
      invoices: [],
      isUpsertDialogOpen: false,
      isDetailsDialogOpen: false,
      upsertError: null,
      isSubmittingTransaction: false,
      selectedTransactionType: 'ADD',
      suppliers: [],
      stockTransactions: [],
      stockTransactionToUpsert: {},
      transactionToView: null,
      isTransactionBeingEdited: false,
      transactionTypes: [],
      pagination: resetPagination(),
      transactionFilters: defaultTransactionFilters(),
      areUserLabelsLoading: false,
      userLabels: [],
      activeView: 'overview',
      stockOverviewItems: [],
      isStockOverviewLoading: false,
      overviewDateFilters: {
        startDate: null,
        endDate: null,
      },
      overviewDayType: 'business',
      initialVariantId: null,
    }
  },
  computed: {
    arenaId() {
      return this.$store.getters?.arena?.id
    },
    viewOptions() {
      return [
        {
          value: 'overview',
          text: this.$t('entities.stockTransactions.toggle.overview'),
        },
        {
          value: 'transactions',
          text: this.$t('entities.stockTransactions.toggle.transactions'),
        },
      ]
    },
  },
  methods: {
    ...mapActions(['showSnackbar']),
    openToolbarTransaction(type) {
      if (!type) return
      this.openUpsertDialog({ type })
    },
    openUpsertDialog({
      transaction = null,
      type = null,
      isTransactionBeingEdited = false,
    } = {}) {
      this.upsertError = null
      this.selectedTransactionType = type
      this.stockTransactionToUpsert = transaction ? { ...transaction } : {}
      this.isTransactionBeingEdited = isTransactionBeingEdited
      this.isUpsertDialogOpen = true
    },
    closeUpsertDialog() {
      this.upsertError = null
      this.isUpsertDialogOpen = false
      this.stockTransactionToUpsert = {}
      this.selectedTransactionType = 'ADD'
      this.isTransactionBeingEdited = false
      this.initialVariantId = null
    },
    openEditTransaction(transaction) {
      this.openUpsertDialog({
        transaction,
        type: transaction.type.code,
        isTransactionBeingEdited: true,
      })
    },
    openTransactionDetails(transaction) {
      if (!transaction) return
      this.transactionToView = { ...transaction }
      this.isDetailsDialogOpen = true
    },
    closeDetailsDialog() {
      this.transactionToView = null
      this.isDetailsDialogOpen = false
    },
    async upsertStockTransaction(payload, transactionType) {
      if (!this.arenaId) return
      this.isSubmittingTransaction = true
      this.upsertError = null
      try {
        if (payload.id) {
          await StockTransaction.api.updateStockTransaction(
            this.arenaId,
            payload.id,
            payload,
          )
        } else {
          const response = await StockTransaction.api.createStockTransaction(
            this.arenaId,
            payload,
          )
          this.displayUpsertToast(payload, transactionType, response.data)
        }
        await Promise.all([
          this.fetchArenaStockTransactions(),
          this.fetchStockOverview(),
        ])
        this.closeUpsertDialog()
      } catch (error) {
        this.handleStockTransactionError(error)
        console.error('Failed to create stock transaction', error)
      } finally {
        this.isSubmittingTransaction = false
      }
    },
    displayUpsertToast(payload, transactionType, createdTransaction = null) {
      const variant = this.arenaVariants.find(
        (it) => it.id === payload.arena_variant_id,
      )
      this.showSnackbar({
        color: 'success',
        message: this.$t(
          `entities.stockTransactions.notifications.${transactionType}`,
          {
            quantity: payload.quantity,
            variantName: variant.name,
            availableStockAfter: createdTransaction.available_stock_after,
          },
        ),
      })
    },
    handleStockTransactionError(error) {
      this.upsertError = error
    },
    async fetchUserLabels() {
      if (!this.stockTransactions.length) {
        this.userLabels = []
        return
      }
      this.areUserLabelsLoading = true
      const transactionCreatorIds = this.stockTransactions.map(
        (it) => it.user_id,
      )
      try {
        const employeeResponse = await ArenasApi.getEmployees(this.arenaId)
        const arenaEmployeeIds = employeeResponse.data
        const userIds = [
          ...new Set([...transactionCreatorIds, ...arenaEmployeeIds]),
        ]
        const { data } = await UsersApi.findByIds(userIds)
        this.userLabels = data
      } catch (error) {
        console.error('Failed to fetch user labels', error)
        this.userLabels = []
      } finally {
        this.areUserLabelsLoading = false
      }
    },
    async fetchArenaSuppliers() {
      if (!this.arenaId) {
        this.suppliers = []
        return
      }
      this.areSuppliersLoading = true
      try {
        const response = await Supplier.api.list(this.arenaId)
        const responseData = response.data?.data
        this.suppliers = Array.isArray(responseData)
          ? responseData.map((supplier) => new Supplier(supplier))
          : []
      } catch (error) {
        console.error('Failed to fetch arena suppliers', error)
        this.suppliers = []
      } finally {
        this.areSuppliersLoading = false
      }
    },
    async fetchArenaInvoices() {
      if (!this.arenaId) {
        this.invoices = []
        return
      }
      this.areInvoicesLoading = true
      try {
        const response = await Invoice.api.getArenaInvoices(this.arenaId)
        const responseData = response.data?.data
        this.invoices = Array.isArray(responseData)
          ? responseData.map((invoice) => new Invoice(invoice))
          : []
      } catch (error) {
        console.error('Failed to fetch arena invoices', error)
        this.invoices = []
      } finally {
        this.areInvoicesLoading = false
      }
    },
    async fetchArenaVariants() {
      if (!this.arenaId) {
        this.arenaVariants = []
        return
      }
      this.areArenaVariantsLoading = true
      try {
        const response = await ArenaProduct.api.list(this.arenaId, {
          size: 100,
        })
        const responseData = response.data?.data
        const products = Array.isArray(responseData)
          ? responseData.map((product) => new ArenaProduct(product))
          : []
        const trackStockProducts = products.filter((it) => it.track_stock)
        this.arenaVariants = trackStockProducts.flatMap((p) =>
          Array.isArray(p.variants) ? p.variants : [],
        )
      } catch (error) {
        console.error('Failed to fetch arena variants', error)
        this.arenaVariants = []
      } finally {
        this.areArenaVariantsLoading = false
      }
    },
    async fetchArenaStockTransactions(params = {}) {
      if (!this.arenaId) {
        this.stockTransactions = []
        this.pagination = resetPagination()
        return
      }
      this.areStockTransactionsLoading = true
      try {
        const response = await StockTransaction.api.getArenaStockTransactions(
          this.arenaId,
          this.getStockTransactionsParams(params),
        )
        const responseData = response?.data?.data
        this.stockTransactions = Array.isArray(responseData)
          ? responseData.map((it) => new StockTransaction(it))
          : []
        const metadata = response?.data?.metadata
        this.updateStockTransactionsPagination(metadata)
        await this.fetchUserLabels()
      } catch (error) {
        console.error(error)
        this.stockTransactions = []
        this.pagination = resetPagination()
      } finally {
        this.areStockTransactionsLoading = false
      }
    },
    async fetchTransactionTypes() {
      this.areTransactionTypesLoading = true
      try {
        const response =
          await StockTransaction.api.getUserStockTransactionTypes()
        const responseData = response.data?.data
        const fetchedTypes = Array.isArray(responseData) ? responseData : []
        const mergedTypes = [...fetchedTypes, ...systemManagedTransactionTypes]
        this.transactionTypes = mergedTypes.map(
          (type) => new StockTransaction(type),
        )
      } catch (error) {
        console.error('Failed to fetch stock transaction types', error)
        this.transactionTypes = []
      } finally {
        this.areTransactionTypesLoading = false
      }
    },
    resetArenaCollections() {
      this.userLabels = []
      this.arenaVariants = []
      this.invoices = []
      this.suppliers = []
      this.stockTransactions = []
      this.pagination = resetPagination()
      this.transactionFilters = defaultTransactionFilters()
    },
    getStockTransactionsParams(overrides = {}) {
      const filters = overrides.filters || {}
      const typeId =
        filters.transactionType ?? this.transactionFilters.transactionType
      const arenaVariantId =
        filters.variantId ?? this.transactionFilters.variantId
      const userId = filters.userId ?? this.transactionFilters.userId
      const createdFrom =
        filters.createdFrom ?? this.transactionFilters.createdFrom
      const createdUntil =
        filters.createdUntil ?? this.transactionFilters.createdUntil
      const page = overrides.page || this.pagination.current_page || 1
      const size = overrides.size || this.pagination.per_page || 10
      return {
        page,
        size,
        ...(typeId ? { type_id: typeId } : {}),
        ...(arenaVariantId ? { arena_variant_id: arenaVariantId } : {}),
        ...(userId ? { user_id: userId } : {}),
        ...(createdFrom ? { created_from: createdFrom } : {}),
        ...(createdUntil ? { created_until: createdUntil } : {}),
      }
    },
    updateStockTransactionsPagination(metadata = null) {
      const page = metadata.page ?? this.pagination.current_page
      const size = metadata.size ?? this.pagination.per_page
      const total = metadata.count
      const lastPage = Math.max(1, Math.ceil(total / size))
      const from = (page - 1) * size + 1
      const to = Math.min(from + size - 1, total)
      this.pagination.current_page = page || 1
      this.pagination.per_page = size
      this.pagination.total = total || 0
      this.pagination.last_page = lastPage
      this.pagination.from = from
      this.pagination.to = to
    },
    handleStockTransactionsPaginationChange({
      page,
      itemsPerPage,
      filters = {},
    }) {
      const normalizeDateFilter = (value) => {
        if (value === undefined) return undefined
        if (value === null || value === '') return null
        return value
      }
      const normalizedPage = Number(page) || 1
      const normalizedItemsPerPage =
        Number(itemsPerPage) || this.pagination.per_page
      const nextTransactionType =
        filters.transactionType !== undefined
          ? filters.transactionType
          : this.transactionFilters.transactionType
      const nextVariantId =
        filters.variantId !== undefined
          ? filters.variantId
          : this.transactionFilters.variantId
      const nextUserId =
        filters.userId !== undefined
          ? filters.userId
          : this.transactionFilters.userId
      const normalizedCreatedFrom = normalizeDateFilter(filters.createdFrom)
      const normalizedcreatedUntil = normalizeDateFilter(filters.createdUntil)
      const nextCreatedFrom =
        normalizedCreatedFrom !== undefined
          ? normalizedCreatedFrom
          : this.transactionFilters.createdFrom
      const nextcreatedUntil =
        normalizedcreatedUntil !== undefined
          ? normalizedcreatedUntil
          : this.transactionFilters.createdUntil
      this.transactionFilters.transactionType = nextTransactionType
      this.transactionFilters.variantId = nextVariantId
      this.transactionFilters.userId = nextUserId
      this.transactionFilters.createdFrom = nextCreatedFrom
      this.transactionFilters.createdUntil = nextcreatedUntil
      this.pagination.current_page = normalizedPage
      this.pagination.per_page = normalizedItemsPerPage
      this.fetchArenaStockTransactions({
        page: normalizedPage,
        size: normalizedItemsPerPage,
        filters: {
          transactionType: nextTransactionType,
          variantId: nextVariantId,
          userId: nextUserId,
          createdFrom: nextCreatedFrom,
          createdUntil: nextcreatedUntil,
        },
      })
    },
    async loadArenaData() {
      if (!this.arenaId) {
        this.resetArenaCollections()
        return
      }
      await Promise.all([
        this.fetchArenaVariants(),
        this.fetchArenaInvoices(),
        this.fetchArenaSuppliers(),
        this.fetchArenaStockTransactions(),
        this.fetchStockOverview(),
      ])
    },
    async loadInitialData() {
      await Promise.all([this.fetchTransactionTypes(), this.loadArenaData()])
    },
    async fetchStockOverview() {
      if (!this.arenaId) {
        this.stockOverviewItems = []
        return
      }
      this.isStockOverviewLoading = true
      try {
        const params = { day_type: this.overviewDayType }
        if (this.overviewDateFilters.startDate) {
          params.date_gte = this.overviewDateFilters.startDate
        }
        if (this.overviewDateFilters.endDate) {
          params.date_lt = this.nextDay(this.overviewDateFilters.endDate)
        }
        const response = await StockTransaction.api.getStockOverview(
          this.arenaId,
          params,
        )
        const responseData = response?.data
        this.stockOverviewItems = Array.isArray(responseData)
          ? responseData
          : []
      } catch (error) {
        console.error('Failed to fetch stock overview', error)
        this.stockOverviewItems = []
      } finally {
        this.isStockOverviewLoading = false
      }
    },
    handleOverviewDateFilterChange({ startDate, endDate }) {
      if (
        this.overviewDateFilters.startDate === startDate &&
        this.overviewDateFilters.endDate === endDate
      ) {
        return
      }

      this.overviewDateFilters.startDate = startDate
      this.overviewDateFilters.endDate = endDate
      if (
        this.overviewDateFilters.startDate &&
        this.overviewDateFilters.endDate
      ) {
        this.fetchStockOverview()
      }
    },
    handleOverviewDayTypeChange(dayType) {
      if (this.overviewDayType === dayType) {
        return
      }
      this.overviewDayType = dayType
      if (
        this.overviewDateFilters.startDate &&
        this.overviewDateFilters.endDate
      ) {
        this.fetchStockOverview()
      }
    },
    handleViewTransactions({ variantId, startDate, endDate }) {
      // Set the transaction filters
      this.transactionFilters.variantId = variantId
      this.transactionFilters.createdFrom = startDate
      this.transactionFilters.createdUntil = endDate
      this.pagination.current_page = 1

      this.activeView = 'transactions'

      // Fetch transactions with the new filters
      this.fetchArenaStockTransactions({
        page: 1,
        size: this.pagination.per_page,
        filters: {
          variantId,
          createdFrom: startDate,
          createdUntil: endDate,
        },
      })
    },
    handleOverviewTransactionOpen({ variantId, transactionType }) {
      this.initialVariantId = variantId
      this.openUpsertDialog({
        type: transactionType,
        isTransactionBeingEdited: false,
      })
    },
    nextDay(dateStr) {
      const [year, month, day] = dateStr.split('-').map(Number)
      const d = new Date(year, month - 1, day)
      d.setDate(d.getDate() + 1)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${dd}`
    },
  },
  watch: {
    arenaId(newArenaId, oldArenaId) {
      if (newArenaId === oldArenaId) return
      if (!newArenaId) {
        this.resetArenaCollections()
        this.stockOverviewItems = []
        return
      }
      this.loadArenaData()
      if (this.activeView === 'overview') {
        this.fetchStockOverview()
      }
    },
  },
  mounted() {
    this.loadInitialData()
  },
  created() {
    const now = new Date()
    this.overviewDateFilters.startDate = `${now.getFullYear()}-${String(
      now.getMonth() + 1,
    ).padStart(2, '0')}-01`
    this.overviewDateFilters.endDate = `${now.getFullYear()}-${String(
      now.getMonth() + 1,
    ).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  },
}
</script>
